import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from "recharts";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatCurrency } from "@/utils/format";
import { CCStat } from "../chartUtils";
import { BAR_HOVER_PROPS, TOOLTIP_CURSOR_BAND } from "@/utils/rechartsInteraction";

/* ─── Tooltip ──────────────────────────────────────────────────────────────── */
interface TipProps {
  active?:  boolean;
  payload?: { value: number; name: string; color: string }[];
  label?:   string;
}
function CustomTooltip({ active, payload, label }: TipProps) {
  if (!active || !payload?.length) return null;
  const orcado    = payload.find((p) => p.name === "Orçado")?.value    ?? 0;
  const realizado = payload.find((p) => p.name === "Realizado")?.value ?? 0;
  const pct       = orcado > 0 ? (realizado / orcado) * 100 : 0;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 min-w-[220px]">
      <p className="text-xs font-bold text-slate-700 mb-2 truncate">{label}</p>
      {payload.map((item) => (
        <div key={item.name} className="flex items-center justify-between gap-4 text-sm py-0.5">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: item.color }} />
            <span className="text-slate-600">{item.name}</span>
          </span>
          <span className="font-semibold text-slate-800">{formatCurrency(item.value, true)}</span>
        </div>
      ))}
      <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
        <span className="text-slate-500">% Executado</span>
        <span
          className="font-bold"
          style={{ color: pct > 100 ? "#ef4444" : pct >= 80 ? "#f59e0b" : "#10b981" }}
        >
          {pct.toFixed(1)}%
        </span>
      </div>
    </div>
  );
}

/* ─── Tick truncado ────────────────────────────────────────────────────────── */
function ShortTick({ x, y, payload }: { x?: number; y?: number; payload?: { value: string } }) {
  const label = (payload?.value ?? "").length > 20
    ? (payload?.value ?? "").slice(0, 18) + "…"
    : (payload?.value ?? "");
  return (
    <text x={x} y={(y ?? 0) + 10} textAnchor="middle" fill="#94a3b8" fontSize={10}>
      {label}
    </text>
  );
}

/* ─── Componente ───────────────────────────────────────────────────────────── */
interface Props {
  data:    CCStat[];
  loading: boolean;
}

export function OrcadoVsRealizadoPorCentro({ data, loading }: Props) {
  return (
    <ChartContainer
      title="Orçado × Realizado por Unidade Construtiva"
      subtitle="Comparativo do orçamento base versus o executado em cada unidade construtiva"
      loading={loading}
      empty={!loading && data.length === 0}
      emptyMessage="Nenhum dado de orçamento disponível para esta obra."
      height={320}
    >
      <BarChart data={data} margin={{ top: 8, right: 20, left: 0, bottom: 40 }} barCategoryGap="30%">
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis
          dataKey="ccName"
          tick={<ShortTick />}
          axisLine={false}
          tickLine={false}
          interval={0}
        />
        <YAxis
          tick={{ fontSize: 11, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
          width={60}
          tickFormatter={(v: number) => formatCurrency(v, true)}
        />
        <Tooltip content={<CustomTooltip />} cursor={TOOLTIP_CURSOR_BAND} />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" iconSize={8} />
        <ReferenceLine y={0} stroke="#e2e8f0" />
        <Bar {...BAR_HOVER_PROPS} dataKey="orcado"    name="Orçado"    fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={48} />
        <Bar {...BAR_HOVER_PROPS} dataKey="realizado" name="Realizado" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={48} />
      </BarChart>
    </ChartContainer>
  );
}
